import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const MEDIA_KEYWORDS = [
  "Imagem descrita",
  "Áudio transcrito",
  "Figurinha descrita",
  "GIF descrita",
  "URL compartilhada",
  "Vídeo descrito",
  "Documento recebido",
  "Reação",
  "Localização",
  "Contato recebido",
];

const typeBadge: Record<string, string> = {
  imagem: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  áudio: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  figurinha: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  gif: "bg-pink-500/20 text-pink-400 border-pink-500/30",
  url: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  vídeo: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  documento: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  outro: "bg-muted text-muted-foreground border-muted",
};

function detectMediaType(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes("imagem descrita")) return "imagem";
  if (lower.includes("áudio transcrito")) return "áudio";
  if (lower.includes("figurinha descrita")) return "figurinha";
  if (lower.includes("gif descrita")) return "gif";
  if (lower.includes("url compartilhada")) return "url";
  if (lower.includes("vídeo descrito")) return "vídeo";
  if (lower.includes("documento recebido")) return "documento";
  return "outro";
}

async function fetchMediaLogs(groupFilter: string, mediaFilter: string) {
  // Build the OR filter for media-related logs
  const orFilter = MEDIA_KEYWORDS.map(k => `message.ilike.%${k}%`).join(",");

  let query = supabase
    .from("syra_logs")
    .select("*")
    .or(orFilter)
    .order("created_at", { ascending: false })
    .limit(100);

  if (groupFilter !== "all") {
    query = query.eq("group_name", groupFilter);
  }

  const { data, error } = await query;
  if (error) throw error;

  let results = data || [];

  if (mediaFilter !== "all") {
    results = results.filter(l => detectMediaType(l.message) === mediaFilter);
  }

  return results;
}

async function fetchGroups() {
  const { data } = await supabase
    .from("syra_logs")
    .select("group_name")
    .not("group_name", "is", null);

  const unique = [...new Set((data || []).map(d => d.group_name).filter(Boolean))];
  return unique.sort();
}

export default function MediaLogs() {
  const [groupFilter, setGroupFilter] = useState("all");
  const [mediaFilter, setMediaFilter] = useState("all");
  const [selectedLog, setSelectedLog] = useState<any | null>(null);

  const { data: groups = [] } = useQuery({ queryKey: ["media-log-groups"], queryFn: fetchGroups });

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["media-logs", groupFilter, mediaFilter],
    queryFn: () => fetchMediaLogs(groupFilter, mediaFilter),
    refetchInterval: 15000,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Eye className="w-6 h-6 text-secondary" />
        <h1 className="text-2xl font-bold text-foreground">Mídia & IA</h1>
      </div>
      <p className="text-sm text-muted-foreground">
        Registro de tudo que a IA interpretou: áudios, imagens, figurinhas, GIFs, links e mais.
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={groupFilter} onValueChange={setGroupFilter}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Grupo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Grupos</SelectItem>
            {groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={mediaFilter} onValueChange={setMediaFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Tipo de mídia" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="áudio">Áudio</SelectItem>
            <SelectItem value="imagem">Imagem</SelectItem>
            <SelectItem value="figurinha">Figurinha</SelectItem>
            <SelectItem value="gif">GIF</SelectItem>
            <SelectItem value="url">URL / Link</SelectItem>
            <SelectItem value="vídeo">Vídeo</SelectItem>
            <SelectItem value="documento">Documento</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <Card><CardContent className="p-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table className="min-w-[700px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Grupo</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Mensagem</TableHead>
                  <TableHead>Detalhes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((l: any) => {
                  const mediaType = detectMediaType(l.message);
                  return (
                    <TableRow key={l.id} className="cursor-pointer" onClick={() => setSelectedLog(l)}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(l.created_at).toLocaleString("pt-BR")}
                      </TableCell>
                      <TableCell className="text-sm">{l.group_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={typeBadge[mediaType] || typeBadge.outro}>
                          {mediaType}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm max-w-[300px] truncate">{l.message}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                        {l.details || "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      Nenhum registro de mídia encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Detalhes da Interpretação
              {selectedLog && (
                <Badge variant="outline" className={typeBadge[detectMediaType(selectedLog.message)] || typeBadge.outro}>
                  {detectMediaType(selectedLog.message)}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedLog && new Date(selectedLog.created_at).toLocaleString("pt-BR")}
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 mt-2">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Grupo</p>
                <p className="text-sm text-foreground">{selectedLog.group_name}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Mensagem</p>
                <p className="text-sm text-foreground whitespace-pre-wrap break-words">{selectedLog.message}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  {detectMediaType(selectedLog.message) === "áudio" ? "Transcrição" :
                   detectMediaType(selectedLog.message) === "url" ? "Descrição do Link" :
                   "Descrição da IA"}
                </p>
                <pre className="text-sm text-foreground bg-muted p-3 rounded-md whitespace-pre-wrap break-words max-h-[300px] overflow-y-auto">
                  {selectedLog.details || "Sem detalhes adicionais"}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
